/**
 * SaveConfirmDialog - Diálogo de confirmación con 3 opciones (Descartar/Cancelar/Guardar)
 *
 * @version 1.0
 * @created 2026-03-13
 * @module core/components
 *
 * @description
 * Diálogo especializado para confirmar acciones cuando hay cambios sin guardar.
 * Ofrece 3 opciones: Descartar cambios, Cancelar acción, o Guardar primero.
 *
 * @remarks
 * Sigue el mismo patrón de animación que ConfirmDialog.
 *
 * @example
 * ```tsx
 * const result = await SaveConfirmDialog.show({
 *   title: '¿Descartar cambios?',
 *   message: 'Tienes cambios sin guardar en la caracterización.',
 * });
 * // result: 'discard' | 'cancel' | 'save'
 * ```
 */

import { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertTriangle, Save, X } from 'lucide-react';
import './save-confirm-dialog.css';

export type SaveConfirmResult = 'discard' | 'cancel' | 'save';

export interface SaveConfirmDialogOptions {
  /**
   * Título del diálogo
   */
  title: string;

  /**
   * Mensaje descriptivo
   */
  message: string;

  /**
   * Texto del botón Descartar
   * @default 'Descartar'
   */
  discardText?: string;

  /**
   * Texto del botón Cancelar
   * @default 'Cancelar'
   */
  cancelText?: string;

  /**
   * Texto del botón Guardar
   * @default 'Guardar'
   */
  saveText?: string;
}

interface SaveConfirmDialogProps extends SaveConfirmDialogOptions {
  onResult: (result: SaveConfirmResult) => void;
}

function SaveConfirmDialogComponent({
  title,
  message,
  discardText = 'Descartar',
  cancelText = 'Cancelar',
  saveText = 'Guardar',
  onResult,
}: SaveConfirmDialogProps): JSX.Element {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = (result: SaveConfirmResult) => {
    setIsClosing(true);
    setTimeout(() => {
      onResult(result);
    }, 300);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose('cancel');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <>
      <div
        className={`save-confirm-dialog-overlay ${isClosing ? 'closing' : ''}`}
        onClick={() => handleClose('cancel')}
      />

      <div className={`save-confirm-dialog ${isClosing ? 'closing' : ''}`}>
        <div className="save-confirm-dialog-content">
          <div className="save-confirm-dialog-header">
            <AlertTriangle size={20} className="save-confirm-dialog-icon" />
            <h3 className="save-confirm-dialog-title">{title}</h3>
          </div>

          <p className="save-confirm-dialog-message">{message}</p>

          <div className="save-confirm-dialog-actions">
            <button
              className="save-confirm-dialog-btn save-confirm-dialog-btn-discard"
              onClick={() => handleClose('discard')}
            >
              <X size={14} />
              {discardText}
            </button>
            <button
              className="save-confirm-dialog-btn save-confirm-dialog-btn-cancel"
              onClick={() => handleClose('cancel')}
            >
              {cancelText}
            </button>
            <button
              className="save-confirm-dialog-btn save-confirm-dialog-btn-save"
              onClick={() => handleClose('save')}
              autoFocus
            >
              <Save size={14} />
              {saveText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export const SaveConfirmDialog = {
  show: (options: SaveConfirmDialogOptions): Promise<SaveConfirmResult> => {
    return new Promise((resolve) => {
      const container = document.createElement('div');
      container.className = 'save-confirm-dialog-container';
      document.body.appendChild(container);
      const root = createRoot(container);

      const cleanup = () => {
        root.unmount();
        document.body.removeChild(container);
      };

      const handleResult = (result: SaveConfirmResult) => {
        cleanup();
        resolve(result);
      };

      root.render(
        <SaveConfirmDialogComponent
          {...options}
          onResult={handleResult}
        />
      );
    });
  },
};
